import { Box, LinkBox, LinkOverlay, Text, VStack } from "@biblioteksentralen/react";
import { getSiteUrl } from "@libry-content/common";
import { AdventCalendarDoor, Publication } from "@libry-content/types";
import { ItemCoverImage } from "../../../searchAndCollection/components/ItemCoverImage";
import { getPublicationUrl } from "../../../utils/getPublicationUrl";
import { useTranslation } from "../../../utils/hooks/useTranslation";
import BlockContent from "../../blockContent/BlockContent";
import { useCommonData } from "../../layout/CommonDataProvider";
import { ShareToSocialMedia } from "../SocialMedia/ShareToSocialMedia";
import { CalendarDoorElement } from "./types";

export const Quote: CalendarDoorElement<"adventCalendarDoorQuote"> = ({ door, date }) => {
  const { site } = useCommonData();
  const shareUrl = site ? `${getSiteUrl(site)}/kampanje/julekalender` : undefined;

  return (
    <>
      <QuoteContent door={door} />
      {shareUrl && (
        <ShareToSocialMedia shareUrl={shareUrl} type="adventCalendarDoorQuote" date={date} marginTop="1.25rem" />
      )}
    </>
  );
};

const Noop = ({ children }: any) => <>{children}</>;

const PublicationWithLink = ({ href, publication }: { href?: string; publication?: Publication }) => {
  const { t } = useTranslation();

  const Wrapper = href ? LinkBox : Noop;
  const TextWrapper = href ? LinkOverlay : Noop;
  const ariaLabel = href
    ? t('Se "{tittel}" i Biblioteksystemet', { tittel: publication?.title ?? "tittel" })
    : undefined;

  return (
    <Wrapper className="group">
      <VStack>
        <Text fontSize={{ base: "xs", md: "sm" }}>{t("Dette sitatet er hentet fra:")}</Text>
        <TextWrapper href={href ?? ""} aria-label={ariaLabel} marginTop="0!important">
          <Text
            fontWeight={600}
            fontSize={{ base: "sm", md: "lg" }}
            padding={{ base: "0 0.75rem 0.5rem", md: "0" }}
            marginTop="0!important"
            _groupHover={{ textDecoration: "underline" }}
          >
            {publication?.title} {t("av")} {publication?.author?.name}
          </Text>
        </TextWrapper>
        {publication?.image?.asset && (
          <ItemCoverImage
            item={publication}
            resolution={250}
            maxWidth={{ base: "4rem", md: "5rem" }}
            marginTop={{ base: "0!important", md: "0.5rem!important" }}
          />
        )}
      </VStack>
    </Wrapper>
  );
};

const QuoteContent = ({ door }: { door: Extract<AdventCalendarDoor, { _type: "adventCalendarDoorQuote" }> }) => {
  const { ts } = useTranslation();
  const { site } = useCommonData();
  const publicationUrl = getPublicationUrl({ publication: door?.publication, site });
  return (
    <VStack gap={{ base: "0.5rem", md: "0" }} marginTop="0!important" minHeight="16rem" justifyContent="space-around">
      <Box
        css={{
          "& div p": {
            margin: "0 0 1.2rem 0",
            fontSize: "xl",
            marginBottom: "0.5rem",
            lineHeight: "1.6rem",

            "&:last-child": {
              margin: 0,
            },
            "&:first-child::before": {
              content: '"«"',
            },
            "&:last-child::after": {
              content: '"»"',
            },
            "@media (max-width: 48rem)": {
              fontSize: "lg",
              marginBottom: "0.5rem",
              lineHeight: "1.4rem",
            },
          },
        }}
        fontSize={{ base: "md", md: "xl" }}
        fontStyle="italic"
        textAlign="center"
      >
        <BlockContent blocks={ts(door.quote)} />
      </Box>
      <PublicationWithLink href={publicationUrl} publication={door?.publication} />
    </VStack>
  );
};
